import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { MFEIntegrationDialog } from '../MFEIntegrationDialog';

interface ButtonFieldProps {
  property: InferredPropertySchema;
  value?: any;
  onChange?: (value: any) => void;
  onPropertyChange?: (key: string, value: any) => void; // Callback to update other properties
  error?: string;
  nodeId?: string;
  workflowId?: string;
}

/**
 * ButtonField - Renders a clickable button for property actions
 * Used for triggering dialogs, actions, or workflows
 */
export function ButtonField({ property, nodeId, workflowId, onPropertyChange }: ButtonFieldProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const params = useParams<{ id: string }>();
  
  // Get workflow ID from props or URL params
  const effectiveWorkflowId = workflowId || params.id;

  const handleClick = async () => {
    // Handle MFE integration button
    if (property.key === 'mfeIntegrationButton') {
      setDialogOpen(true);
      return;
    }

    // Generic button click handler
    console.log(`Button clicked: ${property.key}`);
  };

  return (
    <>
      <div className="space-y-2">
        {property.label && (
          <label className="text-sm font-medium text-foreground">
            {property.label}
            {property.required && <span className="text-destructive ml-1">*</span>}
          </label>
        )}
        
        <Button
          type="button"
          variant="outline"
          onClick={handleClick}
          className="w-full"
        >
          {property.default || 'Click'}
        </Button>
        
        {property.description && (
          <p className="text-xs text-muted-foreground">
            {property.description}
          </p>
        )}
      </div>

      {/* MFE Integration Dialog */}
      {property.key === 'mfeIntegrationButton' && (
        <MFEIntegrationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          workflowId={workflowId}
        />
      )}
    </>
  );
}
